import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Board } from "../lib/api/boards";

interface SidebarProps {
  activeJournal: string;
  onJournalChange: (journal: string) => void;
  boards?: Board[];
  onCreateBoard?: () => void;
}

const Sidebar = ({ activeJournal, onJournalChange, boards = [], onCreateBoard }: SidebarProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  // Get top 3 recent boards
  const recentBoards = [...boards]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);

  const menuItems = [
    {
      id: "home",
      label: t("navigation.home"),
      icon: (
        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
      ),
    },
    {
      id: "connected_channels",
      label: t("navigation.connectedChannels", "Connected Channels"),
      icon: (
        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
      ),
    },
    {
      id: "money_journal",
      label: t("navigation.moneyJournal", "Money Journal"),
      icon: (
        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
      ),
    },
  ];

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="flex-1 px-3 py-4 space-y-7 overflow-y-auto">
        {/* Main Actions */}
        {onCreateBoard && (
          <button
            onClick={onCreateBoard}
            className="w-full flex items-center gap-3 h-9 px-2.5 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 active:scale-95 transition-all shadow-sm group"
          >
            <div className="flex items-center justify-center w-6 h-6 shrink-0 transition-transform group-hover:scale-110">
              <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </div>
            <span className="text-xs font-semibold tracking-tight">{t("boards.createBoard")}</span>
          </button>
        )}


        {/* Navigation Menu */}
        <div>
          <h4 className="px-2.5 mb-2 text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
            {t("navigation.menu", "Navigation")}
          </h4>
          <ul className="space-y-0.5">
            {menuItems.map((item) => (
              <li key={item.id}>
            <button
              id={`onboarding-sidebar-${item.id.replace('_', '-')}`}
              onClick={() => onJournalChange(item.id)}
              className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs transition-all duration-200 ${activeJournal === item.id
                ? "bg-accent-primary/5 dark:bg-accent-primary/10 text-accent-primary font-bold shadow-sm"
                : "text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                }`}
            >
              <div className={`flex items-center justify-center w-6 h-6 rounded-md transition-colors ${activeJournal === item.id ? "bg-accent-primary/10 text-accent-primary" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-400"}`}>
                {item.icon}
              </div>
              <span className="font-semibold">{item.label}</span>
            </button>

          </li>
        ))}
      </ul>
    </div>

    {/* Recent Boards Section */}
    {recentBoards.length > 0 && (
      <div>
        <h4 className="px-2.5 mb-2 text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
          {t("navigation.recent", "Recent Boards")}
        </h4>
        <ul className="space-y-0.5">
          {recentBoards.map((board) => (
            <li key={board.id}>
              <button
                onClick={() => onJournalChange(`board-${board.id}`)}
                className="w-full flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-all text-left truncate group"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-700 group-hover:bg-accent-primary transition-colors shrink-0" />
                <span className="truncate font-semibold">{board.name}</span>
              </button>

            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Support Section */}
    <div>
      <h4 className="px-2.5 mb-2 text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
        {t("navigation.support", "Support")}
      </h4>
      <ul className="space-y-0.5">
        <li>
          <button
            onClick={() => navigate('/docs')}
            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-all"
          >
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-400">
              <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </div>
            <span className="font-semibold">{t("navigation.help", "Help & Docs")}</span>
          </button>

        </li>
      </ul>
    </div>

      </div>
    </div>
  );
};

export default Sidebar;
